import React from 'react';
import { ValidationReport } from '@/components/admin/AssessmentValidator/useAssessmentValidator';
import ActionsDropdown from '@/components/common/ActionsDropdown';
import showToast from '@/components/common/Toast';
import Text from '@/components/ui/Text';
import { SummaryCard } from '@/components/admin/AssessmentValidator/SummaryCard';
import { TCITooltip } from '@/components/ui/Tooltip';
import { formatDate, HAS_IMAGE_STYLE, VERDICT_STYLES, VERDICT_TOOLTIPS } from './AssessmentValidator.utils';

interface ReportDisplayProps {
  loading: boolean;
  onRevalidate: () => void;
  onReset: () => void;
  report: ValidationReport;
}

export const ReportDisplay = ({ loading, onRevalidate, onReset, report }: ReportDisplayProps) => {
  const handleShareReport = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => showToast('Link copied to clipboard!', { position: 'bottom-right' }))
      .catch(() => showToast('Failed to copy link to clipboard.', { position: 'bottom-right' }));
  };

  const actionsDropdown = (
    <ActionsDropdown toggleClassName="btn--purple" toggleText="Actions">
      <button className="dropdown-item full-size" type="button" onClick={handleShareReport}>
        Share Report
      </button>
      <button className="dropdown-item full-size" type="button" onClick={onReset}>
        Validate New Assessment
      </button>
      <button className="dropdown-item full-size" disabled={loading} type="button" onClick={onRevalidate}>
        Revalidate This Assessment
      </button>
    </ActionsDropdown>
  );

  return (
    <div className="tw-mt-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div>
          <Text size="lg" weight="semibold">Validation Report</Text>
          {report.assessmentTitle && (
            <Text size="sm">
              {report.builderUrl ? (
                <a href={report.builderUrl} rel="noopener noreferrer" target="_blank">
                  {report.assessmentTitle}
                </a>
              ) : (
                report.assessmentTitle
              )}
            </Text>
          )}
          {report.generatedAt && (
            <Text size="sm">
              Report Generated: {formatDate(report.generatedAt)}
            </Text>
          )}
          {report.modelName && (
            <Text size="sm">
              Model: {report.modelName}
            </Text>
          )}
        </div>
        {actionsDropdown}
      </div>

      {report.summary && (
        <div className="tw-mb-6">
          <Text className="tw-mb-3">Summary</Text>
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
            <SummaryCard count={report.summary.valid_count} label="Valid" variant="valid" />
            <SummaryCard count={report.summary.invalid_count} label="Invalid" variant="invalid" />
            <SummaryCard count={report.summary.incorrect_count} label="Incorrect" variant="incorrect" />
            <SummaryCard count={report.summary.vague_count} label="Vague" variant="vague" />
          </div>
          <div className="tw-mt-2">
            <Text size="sm">Total Questions: {report.summary.total_questions}</Text>
          </div>
        </div>
      )}

      {report.questions && report.questions.length > 0 && (
        <div>
          <Text className="tw-mb-3">Question Details</Text>
          <div className="tw-space-y-3">
            {report.questions.map(question => (
              <div
                key={question.question_number}
                className="tw-p-4 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-white"
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <span className="tw-flex tw-items-center tw-gap-2">
                    Question {question.question_number}
                    {question.has_image && (
                      <span
                        className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-solid ${HAS_IMAGE_STYLE}`}
                      >
                        <Text size="xs">Has Image</Text>
                      </span>
                    )}
                  </span>
                  <TCITooltip>
                    <TCITooltip.Trigger asChild>
                      <span
                        className={`tw-px-3 tw-py-1 tw-rounded-full tw-border
                          tw-border-solid tw-cursor-help ${VERDICT_STYLES[question.verdict.toLowerCase() || '']}`}
                      >
                        <Text size="xs">{question.verdict}</Text>
                      </span>
                    </TCITooltip.Trigger>
                    <TCITooltip.Content>
                      {VERDICT_TOOLTIPS[question.verdict.toLowerCase() || '']}
                    </TCITooltip.Content>
                  </TCITooltip>
                </div>
                <Text size="sm">
                  {question.rationale}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
